import { CONFIG } from './config.ts';
import type { Rolls } from './rolls.ts';
import { SPECIES } from './species.ts';
import { TRAIT_BY_ID } from './traits.ts';

export interface Stats { hp: number; might: number; guard: number; focus: number; will: number; swift: number }

export function rollMult(r: Rolls, stars = 0): number {
  return r.quality * CONFIG.bloodline[r.bloodline].mult * (r.aura ? CONFIG.auraMult : 1) * (1 + CONFIG.starMult * stars);
}

/**
 * Stat curve: (2*base + iv) * level/100, then the roll multipliers. `refine`
 * (shared/refine.ts) adds to the species' base stats, before talent and every
 * multiplier; index 5 (Speed) is never refined.
 */
export function computeStats(speciesId: string, level: number, r: Rolls, stars = 0, refine?: readonly number[]): Stats {
  const sp = SPECIES[speciesId];
  const m = rollMult(r, stars);
  const trait = TRAIT_BY_ID[r.trait];
  const base = (i: number) => sp.base[i] + (i < 5 ? refine?.[i] ?? 0 : 0);
  const s = (i: number) => Math.floor((((2 * base(i) + r.ivs[i]) * level) / 100 + 5) * m);
  const hp = Math.floor((((2 * base(0) + r.ivs[0]) * level) / 100 + level + 10) * m * (trait?.hpMult ?? 1));
  return { hp, might: s(1), guard: s(2), focus: s(3), will: s(4), swift: s(5) };
}

/**
 * An owned creature's stats on hunts now: level, rolls, stars, refine and stone training (shared/train.ts).
 * PvP never uses this: shared/pvp.ts builds its stats from refine alone, so training stays out of it.
 */
export function creatureStats(c: { species: string; level: number; rolls: Rolls; stars: number; refine?: readonly number[]; train?: readonly number[] }): Stats {
  const add = c.train?.some((x) => x > 0) ? [0, 1, 2, 3, 4].map((i) => (c.refine?.[i] ?? 0) + (c.train?.[i] ?? 0)) : c.refine;
  return computeStats(c.species, c.level, c.rolls, c.stars, add);
}
